import type { SiteContent, GlobalSettings, SEOPageContent } from '../types/cms';
import type { CmsPage } from '../types/cmsPages';

export const SITE_URL = 'https://www.bestneighborhomeservices.com';
export const PUBLIC_PATHS = ['/', '/plans', '/financing', '/service-areas', '/contact', '/privacy-policy', '/terms-of-service', '/blog'];

export function cmsPagePath(page: CmsPage) {
  const prefix = { blog: 'blog', local_service: 'services', ppc_landing: 'landing' }[page.type];
  return `/${prefix}/${encodeURIComponent(page.slug)}`;
}

export function getPageSEO(path: string, content: SiteContent, settings: GlobalSettings, pages: CmsPage[]) {
  const pathname = path === '/' ? '/' : path.replace(/\/$/, '');
  const keys = { '/': 'seoHome', '/plans': 'seoPlans', '/financing': 'seoFinancing', '/service-areas': 'seoServiceAreas', '/contact': 'seoContact' } as const;
  const key = keys[pathname as keyof typeof keys];
  const configured: Partial<SEOPageContent> = key ? content[key] : {};
  const extra: Record<string, { title: string; description: string }> = {
    '/privacy-policy': { title: 'Privacy Policy | Best Neighbor Home Services', description: 'Learn how Best Neighbor Home Services collects, uses, and protects your information, including contact details, website data, and SMS communications.' },
    '/terms-of-service': { title: 'Terms & Conditions | Best Neighbor Home Services', description: 'Read the terms and conditions for Best Neighbor Home Services, including website use, service requests, communications, and our SMS messaging program.' },
    '/blog': { title: 'HVAC Tips & Home Comfort | Best Neighbor Home Services', description: 'Explore heating, cooling, and home comfort tips from Best Neighbor Home Services, your veteran-owned HVAC team serving McKinney and North Texas.' },
    '/admin': { title: 'Site Administration | Best Neighbor Home Services', description: 'Best Neighbor Home Services website administration.' },
  };
  const cmsPage = pages.find(page => cmsPagePath(page) === pathname);
  const exists = Boolean(key || extra[pathname] || cmsPage);
  const title = configured.metaTitle || cmsPage?.meta_title || cmsPage?.title || extra[pathname]?.title || 'Page Not Found | Best Neighbor Home Services';
  const description = configured.metaDescription || cmsPage?.meta_description || extra[pathname]?.description || (exists ? content.seoGlobal.businessDescription : 'Find heating and cooling services, membership plans, and contact information for Best Neighbor Home Services in North Texas.');
  const canonical = new URL(configured.canonicalUrl || pathname, SITE_URL).href;
  const image = new URL(configured.ogImage || content.seoGlobal.defaultOgImage || '/og-image.jpg', SITE_URL).href;
  const global = content.seoGlobal;
  const meta: Record<string, string> = {
    description,
    robots: !exists || pathname === '/admin' ? 'noindex, follow' : configured.robots || 'index, follow',
    'og:title': configured.ogTitle || title,
    'og:description': configured.ogDescription || description,
    'og:image': image,
    'og:image:alt': 'Best Neighbor Home Services — veteran-owned heating and cooling in North Texas',
    'og:type': cmsPage?.type === 'blog' ? 'article' : configured.ogType || 'website',
    'og:url': canonical,
    'og:site_name': global.siteName,
    'og:locale': global.locale || 'en_US',
    'twitter:card': 'summary_large_image',
    'twitter:title': configured.ogTitle || title,
    'twitter:description': configured.ogDescription || description,
    'twitter:image': image,
  };
  if (global.googleVerification) meta['google-site-verification'] = global.googleVerification;
  if (global.bingVerification) meta['msvalidate.01'] = global.bingVerification;
  if (global.twitterHandle) meta['twitter:site'] = global.twitterHandle;
  const business = {
    '@type': global.businessType || 'HVACBusiness', '@id': `${SITE_URL}/#business`,
    name: global.businessName, description: global.businessDescription,
    url: `${SITE_URL}/`, image, telephone: settings.phone, email: settings.email,
    address: { '@type': 'PostalAddress', streetAddress: global.streetAddress, addressLocality: global.city, addressRegion: global.state, postalCode: global.zipCode, addressCountry: global.country },
    geo: { '@type': 'GeoCoordinates', latitude: global.latitude, longitude: global.longitude },
    priceRange: global.priceRange, openingHours: global.openingHours, sameAs: global.sameAs,
    areaServed: ['McKinney', 'Frisco', 'Plano', 'Allen', 'Sherman', 'Greenville', 'Dallas'],
  };
  const jsonLd = { '@context': 'https://schema.org', '@graph': [business, {
    '@type': pathname === '/contact' ? 'ContactPage' : cmsPage?.type === 'blog' ? 'BlogPosting' : 'WebPage',
    '@id': `${canonical}#page`, url: canonical, name: title, description,
    ...(cmsPage?.type === 'blog' ? { headline: cmsPage.title, datePublished: cmsPage.created_at, dateModified: cmsPage.updated_at } : {}),
    about: { '@id': `${SITE_URL}/#business` },
  }] };
  return { title, meta, canonical, jsonLd, exists };
}

export function serializeJSON(value: unknown) {
  return JSON.stringify(value).replace(/</g, '\\u003c').replace(/\u2028/g, '\\u2028').replace(/\u2029/g, '\\u2029');
}

export function renderHead(seo: ReturnType<typeof getPageSEO>) {
  const escape = (value: string) => value.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  return `<title>${escape(seo.title)}</title>\n` +
    Object.entries(seo.meta).map(([key, value]) => `<meta ${key.startsWith('og:') ? 'property' : 'name'}="${key}" content="${escape(value)}" />`).join('\n') +
    `\n<link rel="canonical" href="${escape(seo.canonical)}" />\n<script type="application/ld+json" id="page-jsonld">${serializeJSON(seo.jsonLd)}</script>`;
}
